import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Button, 
  CircularProgress, 
  Alert, 
  Tooltip, 
  LinearProgress,
  Chip
} from '@mui/material';
import { 
  CalendarToday as CalendarIcon,
  Update as UpdateIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PredictionService from '../../services/PredictionService';

// Styled components
const PredictionPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 1.5,
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
}));

const ConfidenceBar = styled(Box)(({ theme, value }) => {
  let barColor;
  if (value >= 0.7) {
    barColor = theme.palette.success.main;
  } else if (value >= 0.4) {
    barColor = theme.palette.warning.main;
  } else {
    barColor = theme.palette.error.main;
  }
  
  return {
    width: '100%',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    '& .MuiLinearProgress-root': {
      height: 8,
      borderRadius: 4,
      backgroundColor: theme.palette.grey[200],
    },
    '& .MuiLinearProgress-bar': {
      borderRadius: 4,
      backgroundColor: barColor,
    }
  };
});

const PredictionChip = styled(Chip)(({ days }) => {
  return {
    fontWeight: 600,
    '& .MuiChip-icon': {
      fontSize: '1.2rem',
    }
  };
});

function ItemPrediction({ itemId, onConsumptionRecorded }) {
  const [prediction, setPrediction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (itemId) {
      // Define fetchPrediction inside useEffect to avoid dependency issues
      const fetchPrediction = async () => {
        try {
          setIsLoading(true);
          setError('');
          
          const data = await PredictionService.getItemPrediction(itemId);
          
          if (data.success) {
            setPrediction(data);
          } else {
            setError(data.message || 'Failed to load prediction data');
          }
        } catch (error) {
          console.error('Error fetching prediction:', error);
          setError('Failed to load prediction data. Please try again later.');
        } finally {
          setIsLoading(false);
        }
      };
      
      fetchPrediction();
    }
  }, [itemId, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    }).format(date);
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.7) {
      return 'High confidence';
    } else if (confidence >= 0.4) {
      return 'Medium confidence';
    } else {
      return 'Low confidence';
    }
  };

  const getConfidenceIcon = (confidence) => {
    if (confidence >= 0.7) {
      return <CheckCircleIcon fontSize="small" />;
    } else if (confidence >= 0.4) {
      return <InfoIcon fontSize="small" />;
    } else {
      return <WarningIcon fontSize="small" />;
    }
  };

  if (isLoading) {
    return (
      <PredictionPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Smart Prediction
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={40} />
        </Box>
      </PredictionPaper>
    );
  }

  if (error) {
    return (
      <PredictionPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Smart Prediction
          </Typography>
        </Box>
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={handleRefresh}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Smart predictions become more accurate as you record more consumption data.
        </Typography>
      </PredictionPaper>
    );
  }

  if (!prediction || !prediction.days_until_depletion) {
    return (
      <PredictionPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Smart Prediction
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 2 }}>
          Not enough data to make a prediction yet.
        </Alert>
        <Typography variant="body2" color="text.secondary" paragraph>
          As you record consumption over time, we'll be able to predict when this item will run out.
        </Typography>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<UpdateIcon />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </PredictionPaper>
    );
  }

  return (
    <PredictionPaper>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <TimelineIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">
            Smart Prediction
          </Typography>
        </Box>
        <Button 
          variant="outlined" 
          color="primary" 
          startIcon={<UpdateIcon />}
          onClick={handleRefresh}
          size="small"
        >
          Refresh
        </Button>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Predicted to run out:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
          <PredictionChip
            icon={<CalendarIcon />}
            label={
              prediction.days_until_depletion === 0
                ? "Today!"
                : prediction.days_until_depletion === 1
                ? "Tomorrow!"
                : `In ${prediction.days_until_depletion} days`
            }
            color={
              prediction.days_until_depletion > 7
                ? "success"
                : prediction.days_until_depletion > 3
                ? "warning"
                : "error"
            }
            variant="outlined"
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
            ({formatDate(prediction.depletion_date)})
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Average daily consumption:
        </Typography>
        <Typography variant="body1" fontWeight="medium">
          {prediction.average_daily_consumption.toFixed(2)} {prediction.unit_of_measure}/day
        </Typography>
      </Box>

      {prediction.usage_frequency && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Usage frequency:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            {prediction.usage_frequency.toFixed(1)} times per week
          </Typography>
        </Box>
      )}

      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
          Prediction confidence:
          <Tooltip title="Higher confidence means more accurate predictions. Confidence improves as you record more consumption data.">
            <InfoIcon fontSize="small" sx={{ ml: 0.5, color: 'action.active' }} />
          </Tooltip>
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1 }}>
            <ConfidenceBar value={prediction.confidence_level}>
              <LinearProgress 
                variant="determinate" 
                value={prediction.confidence_level * 100} 
              />
            </ConfidenceBar>
          </Box>
          <Chip
            icon={getConfidenceIcon(prediction.confidence_level)}
            label={getConfidenceLabel(prediction.confidence_level)}
            size="small"
            color={
              prediction.confidence_level >= 0.7
                ? "success"
                : prediction.confidence_level >= 0.4
                ? "warning"
                : "error"
            }
            sx={{ ml: 1 }}
          />
        </Box>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Last updated: {formatDate(prediction.last_calculated)}
      </Typography>
    </PredictionPaper>
  );
}

export default ItemPrediction;